import { Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useMenuPermissions } from "../hooks/useMenuPermissions";
import { APICore } from "../helpers/api/apiCore";
import { getMenuKeyForRoute } from "./permissionHelper";

/**
 * Permission Route checks both authentication and menu permissions
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string} props.menuKey - Menu key to check permission (optional, will auto-detect from URL)
 * @param {string} props.action - Permission action (read, create, update, delete)
 * @param {boolean} props.requirePermission - Whether to enforce permission check (default: true)
 * @returns {React.ReactNode}
 */
const PermissionRoute = ({ children, menuKey, action = 'read', requirePermission = true }) => {
  const api = new APICore();
  const location = useLocation();
  const { hasPermission, canAccessMenu, loading, userMenus, routeMenuKeyMap } = useMenuPermissions();
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authResult = await api.isUserAuthenticated();
        // console.log("🔐 PermissionRoute auth check result:", authResult);
        setIsAuthenticated(authResult);
      } catch (error) {
        console.error("❌ Auth check error:", error);
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  // Wait for authentication check to complete
  if (isAuthenticated === null) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Checking authentication...</span>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    console.log("❌ User not authenticated, redirecting to login");
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Wait for permissions to load
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading permissions...</span>
        </div>
      </div>
    );
  }

  // Skip permission check if not required
  if (!requirePermission) {
    console.log("⚠️ Permission check skipped for this route");
    return children;
  }

  // Auto-detect menuKey from current path if not provided
  let effectiveMenuKey = menuKey;
  // let effectiveMenuKey = true;
  if (!effectiveMenuKey) {
    effectiveMenuKey = getMenuKeyForRoute(location.pathname, routeMenuKeyMap);
    
    if (!effectiveMenuKey) {
      console.log(`❌ No menuKey found for path: ${location.pathname}, access DENIED by default`);
      // If no menuKey found, redirect to 403 for security
      return <Navigate to="/error-404" state={{ from: location }} replace />;
    }
  }

  // Check if user has the required permission
  const hasAccess = hasPermission(effectiveMenuKey, action);
  // only on local
  // const hasAccess = true
  
  if (!hasAccess) {
    console.log(`❌ Access denied for menu: ${effectiveMenuKey}, action: ${action}, path: ${location.pathname}`);
    // Redirect to 403 page or show inline error
    return <Navigate to="/error-404" state={{ menuKey: effectiveMenuKey, from: location }} replace />;
  }

  // console.log(`✅ Access granted for menu: ${effectiveMenuKey}, action: ${action}, path: ${location.pathname}`);
  return children;
};

export default PermissionRoute;
